<template>
  <span class="app-hotkey" :class="{ 'app-hotkey--disabled': disabled }">
    <v-kbd v-if="modifier" color="surface">{{ formatModifier(modifier) }}</v-kbd>
    <span v-if="modifier && hotkey" class="hotkey-separator">+</span>
    <v-kbd v-if="hotkey" color="surface">{{ hotkey.toUpperCase() }}</v-kbd>
  </span>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import type { PropType } from 'vue'
import { useEventListener } from '@vueuse/core'
import { handleHotkey } from '~/helper/hotkey'
import type { Hotkeys, ModifierKey } from '~/helper/hotkey'

export default defineComponent({
  name: 'HotkeyKbd',
  props: {
    /**
     * Modifier-Taste: 'ctrl', 'alt', 'shift', 'meta'
     */
    modifier: {
      type: String as PropType<ModifierKey | undefined>,
      default: undefined,
      validator: (value?: ModifierKey) => value === undefined || ['ctrl', 'alt', 'shift', 'meta'].includes(value),
    },
    /**
     * Haupttaste (Buchstabe oder Zahl)
     */
    hotkey: {
      type: String as PropType<Hotkeys>,
      required: true,
    },
    /**
     * Ob preventDefault() aufgerufen werden soll
     */
    prevent: {
      type: Boolean,
      default: true,
    },
    /**
     * Ob stopPropagation() aufgerufen werden soll
     */
    stop: {
      type: Boolean,
      default: false,
    },
    /**
     * Deaktiviert die Hotkey-Ausloesung und Darstellung
     */
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: {
    /**
     * Wird ausgelöst, wenn die Tastenkombination gedrückt wird
     */
    pressed: (_: KeyboardEvent) => true,
  },
  setup(props, { emit }) {
    useEventListener('keydown', (ev) => {
      if (props.disabled) {
        return
      }
      return handleHotkey(ev, props.hotkey, () => emit('pressed', ev), {
        modifier: props.modifier,
        prevent: props.prevent,
        stop: props.stop,
      })
    })
  },
  methods: {
    /**
     * Formatiert den Modifier für die Anzeige
     */
    formatModifier(modifier: ModifierKey): string {
      const modifierMap: Record<ModifierKey, string> = {
        ctrl: 'Ctrl',
        alt: 'Alt',
        shift: 'Shift',
        meta: 'Cmd',
      }
      return modifierMap[modifier] || modifier
    },
  },
})
</script>

<style scoped lang="scss">
.app-hotkey {
  display: inline-flex;
  align-items: center;
  gap: 4px;

  .hotkey-separator {
    font-size: 0.875rem;
    color: rgb(var(--v-theme-on-surface));
    opacity: 0.7;
  }
}

.app-hotkey--disabled {
  opacity: 0.5;
}
</style>
